import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import { SelectedTargetItem } from "../../../Enrollment/types";
import {
  classifyEnrollableUser,
  EditionEnrollableUser,
  getEnrollableUserId,
  getEnrollableUserName,
  isEnrollableCase,
  useEditionEnrollableUsers,
} from "../hooks/useEditionEnrollableUsers";
import UserRow, {
  UserRowBadge,
} from "../../../Enrollment/steps/tabs/rows/UserRow";

const PAGE_SIZE = 10;

interface EditionUsersTabProps {
  corporateId: string | number;
  objectId: number;
  editionId: number;
  selected: SelectedTargetItem[];
  onChange: (next: SelectedTargetItem[]) => void;
}

const EditionUsersTab: React.FC<EditionUsersTabProps> = ({
  corporateId,
  objectId,
  editionId,
  selected,
  onChange,
}) => {
  const { t } = useTranslation("edition-enrollment");
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading } =
    useEditionEnrollableUsers({
      corporateId,
      objectId,
      editionId,
      page,
      pageSize,
      fieldFilters,
    });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const lockedIds = useMemo(
    () =>
      new Set<string | number>(
        items
          .filter((u) => !isEnrollableCase(classifyEnrollableUser(u)))
          .map(getEnrollableUserId),
      ),
    [items],
  );

  const badgesFor = (
    item: EditionEnrollableUser,
  ): UserRowBadge[] | undefined => {
    switch (classifyEnrollableUser(item)) {
      case "enrolled-current":
      case "completed-no-edition":
        return [
          { label: String(t("users.enrolled-badge")), variant: "active" },
        ];
      case "enrolled-elsewhere":
        return [
          {
            label: String(
              t("users.enrolled-elsewhere-badge", { edition: item.editionId }),
            ),
            variant: "neutral",
          },
        ];
      case "enrollment-in-progress":
        return [
          { label: String(t("users.in-progress-badge")), variant: "warning" },
        ];
      case "enrollment-error":
        return [{ label: String(t("users.error-badge")), variant: "error" }];
      case "absent-elsewhere":
        return [{ label: String(t("users.absent-badge")), variant: "absent" }];
      default:
        return undefined;
    }
  };

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((u) => getEnrollableUserId(u) === String(id));
      if (!found) return;
      onChange([
        ...selected,
        { id, label: getEnrollableUserName(found), meta: found },
      ]);
    } else {
      onChange(selected.filter((s) => s.id !== id));
    }
  };

  return (
    <Box>
      <SearchableMultiSelectListCst<EditionEnrollableUser>
        items={items}
        getId={(u) => getEnrollableUserId(u)}
        renderRow={({ item }) => (
          <UserRow
            id={getEnrollableUserId(item)}
            name={getEnrollableUserName(item)}
            email={item.userEmail}
            extId={item.userExtId}
            badges={badgesFor(item)}
          />
        )}
        selectedIds={selectedSet}
        lockedIds={lockedIds}
        onToggle={handleToggle}
        onSearchSubmit={() => {}}
        searchPlaceholder=""
        searchButtonLabel=""
        hideSearch
        headerSlot={
          <>
            <FiltersAccordionToggle
              pageId={PagesID["enrollment.edition.user"]}
              value={fieldFilters}
              onApply={(next) => {
                setFieldFilters(next);
                setPage(1);
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "12px 16px",
                borderRadius: "4px",
                backgroundColor: theme.palette.badge.info.background,
                border: `1px solid ${theme.palette.badge.info.border}`,
              }}
            >
              <Info
                size={18}
                color={theme.palette.badge.info.border}
                style={{ flexShrink: 0 }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: theme.palette.common.black,
                  }}
                >
                  {t("users-edition-banner-title")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.3,
                    marginTop: "8px",
                    color: theme.palette.common.black,
                  }}
                >
                  {t("users-edition-banner-text")}
                </Typography>
              </Box>
            </Box>
          </>
        }
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        isLoading={isLoading}
        selectedCountLabel={String(
          t("users.selected-count", { count: selected.length }),
        )}
        emptyLabel={String(t("users.empty"))}
      />
    </Box>
  );
};

export default EditionUsersTab;
